<script setup>
/* global axios */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  attachmentId: { type: Number, required: true },
  source: { type: Object, required: true },
});
const emit = defineEmits(['transcript']);
const { accountId, currentAccount } = useAccount();
const { t } = useI18n();
const state = ref(null);
const unavailable = ref(false);
const busy = ref(false);
let generation = 0;
let timer;
let controller;

const label = computed(() => {
  if (unavailable.value) return t('TEAM_LILITH.AUDIO.STATE_UNAVAILABLE');
  if (!state.value || ['DONE', 'DISABLED'].includes(state.value.status)) return '';
  const key = state.value.reason || state.value.status;
  return t(`TEAM_LILITH.AUDIO.${key}`);
});

const load = async (retry = false) => {
  if (busy.value) return;
  const current = generation;
  const id = props.attachmentId;
  const account = accountId.value;
  busy.value = true;
  clearTimeout(timer);
  controller = new AbortController();
  try {
    const url = `/api/v1/accounts/${account}/lilith/audio_transcriptions/${id}`;
    const options = { signal: controller.signal };
    const response = retry
      ? await axios.post(`${url}/retry`, {}, options)
      : await axios.get(url, options);
    if (current !== generation) return;
    state.value = response.data;
    unavailable.value = false;
    emit('transcript', response.data.status === 'DONE' ? response.data.text : '');
    if (['PENDING', 'RUNNING'].includes(response.data.status)) {
      timer = setTimeout(() => load(), 5000);
    }
  } catch (error) {
    if (current !== generation || error.code === 'ERR_CANCELED') return;
    unavailable.value = true;
    // Revoked access clears old text; a transport failure preserves the last
    // known transcript and offers an explicit refresh without triggering AI.
    if ([401, 403, 404].includes(error.response?.status)) {
      state.value = null;
      emit('transcript', '');
    }
  } finally {
    if (current === generation) busy.value = false;
  }
};

watch([
  accountId,
  () => props.attachmentId,
  () => currentAccount.value?.settings?.audio_transcriptions,
  () => JSON.stringify([props.source.dataUrl, props.source.transcribedText,
    props.source.audioTranscription || props.source.audio_transcription]),
], () => {
  generation += 1;
  controller?.abort();
  clearTimeout(timer);
  state.value = null;
  unavailable.value = false;
  busy.value = false;
  emit('transcript', null);
  load();
}, { immediate: true });

onBeforeUnmount(() => {
  generation += 1;
  controller?.abort();
  clearTimeout(timer);
});
</script>

<template>
  <div v-if="label" class="px-3 pb-2 text-xs text-n-slate-11" aria-live="polite">
    <span>{{ label }}</span>
    <button v-if="unavailable || state?.can_retry" type="button" :disabled="busy"
      class="ml-2 border-0 bg-transparent p-0 text-n-brand disabled:opacity-50"
      @click="load(!unavailable)">
      {{ t(unavailable ? 'TEAM_LILITH.AUDIO.REFRESH' : 'TEAM_LILITH.AUDIO.RETRY') }}
    </button>
  </div>
</template>
